import {
  SESSION_FEEDBACK_TYPE_TUTOR,
  SessionAttendeeRole,
  STUDY_SPACES_TOPIC,
  TutorSessionFeedbackRating,
} from '@/queries/constants'
import SeriesQ from '@/queries/SeriesQ'
import SessionAttendeeQ from '@/queries/SessionAttendeeQ'
import SessionFeedbackQ from '@/queries/SessionFeedbackQ'
import SessionQ from '@/queries/SessionQ'
import SessionRegistrationQ from '@/queries/SessionRegistrationQ'
import SessionTagQ from '@/queries/SessionTagQ'
import TopicQ from '@/queries/TopicQ'
import TutorAuditQ from '@/queries/TutorAuditQ'
import { SQL, type SQLFragment } from '@/client/query'
import {
  HIGHLY_ENGAGING_SESSION_MIN_TOTAL_TALK_TIME_SECONDS,
  HIGHLY_ENGAGING_SESSION_TALK_TIME_PERCENTAGE,
} from '@/client/services/session'

import {
  type AutoGeneratedSessionTag,
  LONG_ONE_ON_ONE_TIME_SEC,
  MANY_UNAUDITED_SESSIONS_THRESHOLD,
} from './constants'
import { SessionTag as SessionTagType } from './types'

const NPS_RATING_THRESHOLD = 6.5
const NPS_COUNT_THRESHOLD = 3
const NPS_REGISTRATIONS_THRESHOLD = 4
const LOW_ENGAGEMENT_THRESHOLD = 0.2
const LOW_ENGAGEMENT_MIN_DATE_QUERY = SQL`'February 1, 2024'`
const LOW_NPS_SERIES_EXCLUDE_THRESHOLD_DAYS = 14

export const SESSION_TAG_TO_QUERY: Record<AutoGeneratedSessionTag, SQLFragment<boolean>> = {
  // Session has 2+ Not Helpfuls, or 1+ Not Helpful and < 4 learners
  [SessionTagType.NotHelpfulRatings]: SQL`(
    (SELECT
      COUNT(*) > 1 OR (
        COUNT(*) = 1 AND
        (SELECT COUNT(DISTINCT ${SessionAttendeeQ.user_id}) < 4 ${SessionQ.getZoomAttendees()})
      )
      ${SessionFeedbackQ.where({
        sessionId: SessionQ.id,
        rating: TutorSessionFeedbackRating.NotHelpful,
        type: SESSION_FEEDBACK_TYPE_TUTOR,
      })}
    )
    AND NOT ${SessionQ.isSATSeriesSession()}
  )`,

  // The first or second session hosted by this tutor
  [SessionTagType.FirstTwoSessions]: SQL`(
    (
      SELECT COUNT(*) <= 0 FROM ${SessionQ} s2
      WHERE s2.deleted_at IS NULL
        AND s2.user_id = ${SessionQ.user_id}
        AND s2.start_date < ${SessionQ.start_date}
        AND EXISTS(SELECT 1 ${SessionAttendeeQ.where({
          sessionId: SQL`s2.id`,
          roles: [SessionAttendeeRole.Learner],
        })})
    )
    AND NOT ${SessionQ.isSATSeriesSession()}
  )`,

  [SessionTagType.LongOneOnOne]: SQL`(
      ${SessionQ.isOneOnOne(SessionQ.id)} AND
      (
        SELECT SUM(${SessionAttendeeQ.duration_seconds}) > ${LONG_ONE_ON_ONE_TIME_SEC}
        ${SessionAttendeeQ.where({
          roles: [SessionAttendeeRole.Learner],
          sessionId: SessionQ.id,
        })}
      )
    )`,

  [SessionTagType.LowEngagement]: SQL`(
    (SELECT
      SUM(${SessionAttendeeQ.speaking_duration_seconds}) BETWEEN 1
        AND (${LOW_ENGAGEMENT_THRESHOLD} * EXTRACT(EPOCH FROM
          MAX(${SessionAttendeeQ.joined_at} + ${SessionAttendeeQ.duration_seconds} * INTERVAL '1 SECOND')
            FILTER (WHERE ${SessionAttendeeQ.hasRole(SessionAttendeeRole.Learner)})
           - MIN(${SessionAttendeeQ.joined_at})
            FILTER (WHERE ${SessionAttendeeQ.hasRole(SessionAttendeeRole.Learner)})
        ))
        ${SessionAttendeeQ.where({
          sessionId: SessionQ.id,
          roles: [SessionAttendeeRole.Learner, SessionAttendeeRole.Tutor],
        })}
    )
    AND (${SessionQ.series_id} IS NULL OR NOT ${SessionQ.isDialoguesSeriesSession()})
    AND ${TopicQ.name} != ${STUDY_SPACES_TOPIC}
    AND ${SessionQ.zoom_end_date} IS NOT NULL
    AND ${SessionQ.start_date} > ${LOW_ENGAGEMENT_MIN_DATE_QUERY}
    AND (
        ${SessionQ.series_id} IS NULL OR
        NOT ${SessionQ.isSATSeriesSession()}
    )
  )`,

  // It's been at least 7 sessions since their last audit, or the last session that's been given this flag.
  [SessionTagType.ManyUnauditedSessions]: SQL`(
        (
            SELECT (
                WITH last_audit AS (
                    SELECT
                       COALESCE(MAX(${TutorAuditQ.created_at}), NOW() - INTERVAL '1000 YEAR') AS date
                    ${TutorAuditQ.where({
                      userId: SessionQ.user_id,
                    })}
                ),
                last_flag AS (
                    SELECT
                      COALESCE(MAX(${SessionTagQ.created_at}), NOW() - INTERVAL '1000 YEAR') AS date
                    FROM ${SessionTagQ}
                    JOIN ${SessionQ} s2 ON ${SessionTagQ.session_id} = s2.id
                    WHERE s2.user_id = ${SessionQ.user_id}
                    AND ${SessionTagQ.tag} = ${SessionTagType.ManyUnauditedSessions}
                ),
                session_threshold AS (
                    SELECT
                        COUNT(*) >= ${MANY_UNAUDITED_SESSIONS_THRESHOLD} as passed_sessions
                    FROM ${SessionQ} s2, last_audit, last_flag
                    WHERE s2.deleted_at IS NULL
                    AND s2.user_id = ${SessionQ.user_id}
                    AND s2.start_date > last_audit.date AND s2.start_date > last_flag.date
                    AND s2.start_date < ${SessionQ.start_date}
                )
                SELECT passed_sessions FROM session_threshold
            )
        )
        AND NOT ${SessionQ.isSATSeriesSession()}
    )`,

  // Tutor likely did not show up
  [SessionTagType.TutorNoShow]: SQL`${SessionQ.getComputedEndDate()} < NOW() AND
  (${SessionQ.attempted_hosting} IS FALSE) AND
  EXISTS(SELECT 1 ${SessionRegistrationQ.where(
    { sessionId: SessionQ.id },
    SQL`${SessionRegistrationQ.attempted_joining}`
  )})`,

  // Tutor has at least one audit that was escalated (ignore sessions that are already escalated for safety)
  [SessionTagType.EscalatedToMentor]: SQL`EXISTS(SELECT 1 ${TutorAuditQ.where(
    { sessionId: SessionQ.id },
    SQL`(${TutorAuditQ.escalatedToMentor()}) AND (${TutorAuditQ.isSafeEnvironment()})`
  )})`,

  // Tutor has at least one audit with a poor rating
  [SessionTagType.LowRating]: SQL`EXISTS(SELECT 1 ${TutorAuditQ.where(
    { sessionId: SessionQ.id },
    TutorAuditQ.hasPoorRating()
  )})`,

  // In a series with multiple low NPS ratings, is a session where NPS was
  // updated, and no other sessions within LOW_NPS_SERIES_EXCLUDE_THRESHOLD_DAYS days were flagged
  [SessionTagType.LowNPSSeries]: SQL`
    (
      SELECT
        COALESCE(${SeriesQ.getAverageNPSRating()} <= ${NPS_RATING_THRESHOLD}, FALSE)
        AND (
          ${SeriesQ.getNPSRatingCount()} >= ${NPS_COUNT_THRESHOLD}
          OR (SELECT ${SessionQ.countSessionRegistration()}) < ${NPS_REGISTRATIONS_THRESHOLD}
        )
      ${SeriesQ.where({ id: SessionQ.series_id })}
    ) AND (
      (SELECT ${SessionQ.countRatingsWithNPS()}) >= 1
    ) AND NOT EXISTS (
      SELECT 1 FROM ${SessionQ} s2
      WHERE s2.deleted_at IS NULL
        AND s2.series_id = ${SessionQ.series_id}
        AND s2.start_date > ${SessionQ.start_date} - ${LOW_NPS_SERIES_EXCLUDE_THRESHOLD_DAYS} * INTERVAL '1 DAY'
        AND s2.start_date < NOW()
        AND (
          EXISTS(SELECT 1 ${SessionTagQ.where({
            sessionId: SQL`s2.id`,
            tag: SessionTagType.LowNPSSeries,
          })})
          OR EXISTS(SELECT 1 ${TutorAuditQ.where({ sessionId: SQL`s2.id` })})
        )
    )
 `,

  [SessionTagType.HighlyEngagingSession]: SQL`
    ${SessionQ.zoom_end_date} IS NOT NULL
    AND (SELECT COUNT(*) >= 1 ${SessionAttendeeQ.where({
      sessionId: SessionQ.id,
      roles: [SessionAttendeeRole.Learner],
    })})
    AND (${SessionQ.happenedBeforeTalkTimeMigration()} OR (
      (
        SELECT SUM(${
          SessionAttendeeQ.speaking_duration_seconds
        }) >= ${HIGHLY_ENGAGING_SESSION_MIN_TOTAL_TALK_TIME_SECONDS}
        ${SessionAttendeeQ.where({
          sessionId: SessionQ.id,
          roles: [SessionAttendeeRole.Tutor, SessionAttendeeRole.Learner],
        })}
      )
      AND ${SessionQ.getTotalTutorSpeakingPercentage()} BETWEEN 0 AND ${HIGHLY_ENGAGING_SESSION_TALK_TIME_PERCENTAGE}
    ))
  `,

  [SessionTagType.AIRiskHigh]: SQL`${SessionQ.safety_risk_automated} = 'high'`,
} as const
